
import { Store } from '@reduxjs/toolkit';
import API from 'sdk/base/api-provider';
import SDKResponser from 'sdk/base/responses';
import ApiHandler from 'sdk/base/api-handler';
/**
 * Represents a base module for managing state and dispatching actions.
 */
export class BaseModule {
    /**
     * Creates an instance of BaseModule.
     * @param store - The Redux store instance.
     * @param actions - A record of action types.
     * @param sliceName - The name of the state slice.
     */
    constructor(store: Store, actions: Record<string, string>, sliceName: string) {
        this.store = store;
        this.sdkResponser = new SDKResponser(this._dispatchError, sliceName);
        this.sliceName = sliceName;
        this.actions = actions;
    }
    /**
     * Retrieves the current state of the specified slice.
     * @returns The state of the slice.
     */
    getState() {
        return this.store.getState()[this.sliceName];
    };

    /**
     * Dispatches an action to the Redux store.
     * @param action - The action type to dispatch.
     * @param payload - The optional payload for the action.
     * @returns 1 if the action was dispatched, otherwise 0.
     */
    dispatchAction = (action: string, payload?: any) => {
        if (!this.actions[action]) {
            return 0;
        }
        this.store.dispatch({ type: this.actions[action], payload });
        return 1;
    };

    /**
     * Dispatches an error action to the Redux store.
     * @param errorCode - The optional error code.
     * @param description - The optional error description.
     */
    _dispatchError = (errorCode?: string, description?: string) => {
        if (errorCode) {
            this.dispatchAction(this.actions.UPDATE_API_ERROR, { errorCode, description });
        } else {
            this.dispatchAction(this.actions.CLEAR_API_ERROR);
        }
    };

    /**
     * Calls the API handler with the provided data and optionally handles errors via a feedback handler.
     * Supports both single API data objects and arrays of API data objects.
     * 
     * @template T - The expected type of the API response.
     * @param data - The API data object or an array of API data objects.
     * @param feedbackHandler - An optional error feedback handler for managing API errors.
     * @returns A promise resolving to:
     *   - T if `data` is a single API data object.
     *   - T[] if `data` is an array of API data objects.
     * 
     * @example
     * const response = await call<SomeType>(singleApiData);
     * // response is of type SomeType
     * 
     * const responses = await call<SomeType[]>(multipleApiData);
     * // responses is of type SomeType[]
    */
    call = async <T>(data: API | API[]): Promise<T | null> => {
        if (Array.isArray(data)) {
            return Promise.all(data.map((d) => ApiHandler.call(d))) as Promise<T>;
        } 
        return ApiHandler.call(data) as Promise<T | null>;
    };


    store: Store;
    sliceName: string;
    actions: Record<string, string>;
    sdkResponser: SDKResponser;
}

export default BaseModule;
